import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  MagnifyingGlassIcon, 
  XMarkIcon, 
  FunnelIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import Button from './Button';

export interface SearchField {
  key: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'daterange';
  options?: { value: string; label: string }[];
  placeholder?: string;
}

export interface SearchFilters {
  [key: string]: any;
}

interface AdvancedSearchProps {
  fields: SearchField[];
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  onSearch: () => void;
  onClear: () => void;
  isOpen: boolean;
  onClose: () => void;
}

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  fields,
  filters,
  onFiltersChange,
  onSearch,
  onClear,
  isOpen,
  onClose,
}) => {
  const [localFilters, setLocalFilters] = useState<SearchFilters>(filters);

  const handleFieldChange = (key: string, value: any) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    onSearch();
    onClose();
  };

  const handleClear = () => {
    const clearedFilters: SearchFilters = {};
    fields.forEach(field => {
      clearedFilters[field.key] = '';
    });
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    onClear();
    onClose();
  };

  const renderField = (field: SearchField) => {
    const value = localFilters[field.key] || '';

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            className="form-input"
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
          />
        );

      case 'select':
        return (
          <select
            className="form-input"
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
          >
            <option value="">All {field.label}</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            className="form-input"
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
          />
        );

      case 'daterange':
        return (
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              className="form-input"
              placeholder="From"
              value={value.from || ''}
              onChange={(e) => handleFieldChange(field.key, { ...value, from: e.target.value })}
            />
            <input
              type="date"
              className="form-input"
              placeholder="To"
              value={value.to || ''}
              onChange={(e) => handleFieldChange(field.key, { ...value, to: e.target.value })}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 flex items-center">
                    <FunnelIcon className="h-5 w-5 mr-2" />
                    Advanced Search & Filters
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {fields.map(field => (
                    <div key={field.key}>
                      <label className="form-label">
                        {field.label}
                      </label>
                      {renderField(field)}
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center pt-6 mt-6 border-t border-gray-200">
                  <Button
                    variant="secondary"
                    onClick={handleClear}
                    className="flex items-center"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-2" />
                    Clear All
                  </Button>
                  
                  <div className="flex space-x-3">
                    <Button
                      variant="secondary"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleApply}
                      className="flex items-center"
                    >
                      <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AdvancedSearch;
